// ─────────────────────────────────────────────
// KLOTHIQ — Accounts Period Selector
// ─────────────────────────────────────────────

import DateRangeFields from '@/components/common/DateRangeFields';
import { useState } from 'react';
import { Calendar } from 'lucide-react';
import {
  PeriodPreset,
  PERIOD_LABELS,
  getPresetRange,
  formatDisplayDate,
} from '@/utils/cashbookCalc';

interface PeriodSelectorProps {
  from: string | null;
  to: string | null;
  preset?: PeriodPreset;
  onChange: (range: { from: string | null; to: string | null; preset: PeriodPreset }) => void;
  className?: string;
}

const PRESET_ORDER: PeriodPreset[] = ['month', 'last_month', 'week', 'fy', 'all', 'custom'];

export default function PeriodSelector({
  from,
  to,
  preset = 'month',
  onChange,
  className = '',
}: PeriodSelectorProps) {
  const [isCustomOpen, setIsCustomOpen] = useState(preset === 'custom');
  const [customFrom, setCustomFrom] = useState(from || '');
  const [customTo, setCustomTo] = useState(to || '');

  const handleSelectPreset = (p: PeriodPreset) => {
    if (p === 'custom') {
      setIsCustomOpen(true);
      onChange({ from: customFrom || from, to: customTo || to, preset: 'custom' });
      return;
    }
    setIsCustomOpen(false);
    const range = getPresetRange(p);
    onChange({ ...range, preset: p });
  };

  const handleApplyCustom = (e: React.FormEvent) => {
    e.preventDefault();
    onChange({
      from: customFrom || null,
      to: customTo || null,
      preset: 'custom',
    });
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {/* Preset Buttons */}
      <div className="inline-flex flex-wrap rounded-lg border border-border bg-card/60 p-0.5 shadow-sm">
        {PRESET_ORDER.map((p) => {
          const isActive = preset === p;
          return (
            <button
              key={p}
              type="button"
              onClick={() => handleSelectPreset(p)}
              className={`min-h-11 rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          );
        })}
      </div>

      {/* Date Range Badge / Custom Inputs */}
      {isCustomOpen ? (
        <form onSubmit={handleApplyCustom} className="flex w-full flex-wrap items-end gap-2 text-xs sm:w-auto">
          <DateRangeFields
            from={customFrom}
            to={customTo}
            onFromChange={(value) => setCustomFrom(value ?? '')}
            onToChange={(value) => setCustomTo(value ?? '')}
          />
          <button
            type="submit"
            className="min-h-11 rounded-xl bg-secondary px-4 text-sm font-medium text-secondary-foreground hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Apply
          </button>
        </form>
      ) : (
        <div className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-2.5 py-1 text-xs text-muted-foreground">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground/80" />
          <span>
            {from ? formatDisplayDate(from) : 'Start'}
            {' — '}
            {to ? formatDisplayDate(to) : 'Present'}
          </span>
        </div>
      )}
    </div>
  );
}
